// #region Imports
import { reactive, ref } from "vue";
// #endregion
export default class SecurityFactory {
    // #region Vars
    _appHost;
    _cache = new Map();
    _queue = ref([]);
    // #endregion
    // #region Properties
    get hasUser() {
        return this._appHost.store.get("session").isAuthenticated;
    }
    get currentUser() {
        return this._appHost.store.get("session").currentUser;
    }
    // #endregion
    // #region Constructor
    constructor(appHost) {
        // #region Store
        this._appHost = appHost;
        // #endregion
    }
    // #endregion
    // #region Core
    hasRole(...roles) {
        if (roles.length === 0) {
            return false;
        }
        let tUserRoles = this.currentUser?.roles;
        {
            if (!tUserRoles) {
                return false;
            }
            if (tUserRoles.length === 0) {
                return false;
            }
        }
        for (let tRole1 of roles) {
            if (utils.isArray(tRole1)) {
                for (let tRole2 of tRole1) {
                    if (tUserRoles.some(u => u.toLowerCase() === tRole2.toLowerCase())) {
                        return true;
                    }
                }
            }
            else {
                if (tUserRoles.some(u => u.toLowerCase() === tRole1.toLowerCase())) {
                    return true;
                }
            }
        }
        return false;
    }
    // #endregion
    // #region Check
    async allowWithFallback(permission, security, data = undefined) {
        // #region Data
        if (!permission?.right) {
            return true;
        }
        if (!security?.permission && !permission?.permission) {
            return true;
        }
        // #endregion
        // #region Optimize
        let tRight = permission?.right;
        {
            if (tRight && tRight === '$') {
                tRight = security?.right;
            }
            if (!tRight || tRight === '-') {
                return true;
            }
            if (tRight === "CreateOrUpdate") {
                tRight = (data?.__g_proxy?.isNew ?? false) ? "Create" : "Update";
            }
        }
        // #endregion
        // #region New
        var tInstance = {
            permission: permission?.permission ?? security?.permission,
            option: permission?.option ?? (security?.option ?? "Form"),
            params: [],
        };
        if (permission?.params) {
            permission.params.forEach(f => {
                tInstance.params.push({
                    name: f.name,
                    expression: f.expression,
                    member: f.member,
                    value: f.value
                });
            });
        }
        if (security?.params) {
            security.params.forEach(f => {
                tInstance.params.push({
                    name: f.name,
                    expression: f.expression,
                    member: f.member,
                    value: f.value
                });
            });
        }
        // #endregion
        // #region Check
        if (tInstance.params && tInstance.params.length > 0 && !data) {
            return false;
        }
        // #endregion
        return await this.allow(tInstance, undefined, tRight, data);
    }
    async allow(permission, option, right, args = null) {
        // #region Check
        if (!permission) {
            return true;
        }
        // #endregion
        // #region Optimize
        let tPermission = undefined;
        {
            if (utils.isString(permission)) {
                tPermission = {
                    permission: permission.toString(),
                    option: option ?? "Form",
                    params: []
                };
            }
            else {
                tPermission = permission;
            }
        }
        // #endregion
        // #region Instance
        let tInstance = {
            securityKey: "",
            permission: tPermission.permission,
            option: tPermission.option,
            AllowRead: false,
            AllowCreate: false,
            AllowDelete: false,
            AllowEdit: false,
            params: {}
        };
        if (tPermission.params && tPermission.params.length > 0) {
            // #region Build
            tPermission.params.forEach(f => {
                let tValue = undefined;
                {
                    if (f.member) {
                        tValue = utils.getObjectValue(args, f.member);
                    }
                    else if (f.expression) {
                        tValue = utils.evalParams(f.expression, { self: args });
                    }
                    else if (f.value) {
                        tValue = f.value;
                    }
                }
                tInstance.params[f.name] = tValue;
            });
            // #endregion
        }
        var tUrlParams = new URLSearchParams();
        {
            if (tInstance.params) {
                Object.keys(tInstance.params).forEach(k => {
                    tUrlParams.set(k, tInstance.params[k]);
                });
            }
        }
        //Set
        {
            tInstance.securityKey = (tInstance.permission + "__" + tInstance.option + (tUrlParams.size > 0 ? "__" + tUrlParams.toString() : '')).toLowerCase();
        }
        // #endregion
        // #region Find
        let tCachedItem = this._cache.get(tInstance.securityKey);
        {
            if (tCachedItem) {
                if (right === "Delete") {
                    return tCachedItem.AllowDelete;
                }
                else if (right === "Update" || right === "CreateOrUpdate") {
                    return tCachedItem.AllowEdit;
                }
                else if (right === "Create") {
                    return tCachedItem.AllowCreate;
                }
                else {
                    return tCachedItem.AllowRead;
                }
            }
            else {
                let tResult = await this._appHost.api.get("system/app/permission", { params: { permission: tInstance.permission, option: tInstance.option, ...tInstance.params } });
                {
                    if (!tResult.data) {
                        return false;
                    }
                }
                //Data
                {
                    tInstance.AllowCreate = tResult.data.allowCreate;
                    tInstance.AllowDelete = tResult.data.allowDelete;
                    tInstance.AllowEdit = tResult.data.allowEdit;
                    tInstance.AllowRead = tResult.data.allowRead;
                }
                //Cache
                {
                    this._cache.set(tInstance.securityKey, tInstance);
                }
                if (right === "Delete") {
                    return tInstance.AllowDelete;
                }
                else if (right === "Update" || right === "CreateOrUpdate") {
                    return tInstance.AllowEdit;
                }
                else if (right === "Create") {
                    return tInstance.AllowCreate;
                }
                else {
                    return tInstance.AllowRead;
                }
            }
        }
        // #endregion
    }
    // #endregion
    // #region Evaluate
    evaluate(Key) {
        this.evaluateAsync(Key).then(tInstance => {
            // #region Check            
            if (!tInstance) {
                return;
            }
            // #endregion
            // #region Notifiy
            while (true) {
                let tItem = this._queue.value.find(e => e.key === tInstance.securityKey);
                {
                    if (!tItem) {
                        break;
                    }
                }
                if (tItem.right === "Delete") {
                    tItem.isAllowed = tInstance.AllowDelete;
                }
                else if (tItem.right === "Update" || tItem.right === "CreateOrUpdate") {
                    tItem.isAllowed = tInstance.AllowEdit;
                }
                else if (tItem.right === "Create") {
                    tItem.isAllowed = tInstance.AllowCreate;
                }
                else {
                    tItem.isAllowed = tInstance.AllowRead;
                }
                //Remove
                this._queue.value.splice(this._queue.value.indexOf(tItem), 1);
            }
            // #endregion
        });
    }
    async evaluateAsync(Key) {
        // #region Get
        let tInstance = this._cache.get(Key);
        {
            if (!tInstance) {
                return undefined;
            }
            if (tInstance.status === "evaluated") {
                return tInstance;
            }
            else if (tInstance.status) {
                return undefined;
            }
        }
        // #endregion
        // #region Load
        {
            tInstance.status = "working";
        }
        let tResult = await this._appHost.api.get("system/app/permission", { params: { permission: tInstance.permission, option: tInstance.option, ...tInstance.params } });
        {
            //Set
            {
                tInstance.status = "evaluated";
            }
            if (tResult.data) {
                tInstance.AllowCreate = tResult.data.allowCreate;
                tInstance.AllowDelete = tResult.data.allowDelete;
                tInstance.AllowEdit = tResult.data.allowEdit;
                tInstance.AllowRead = tResult.data.allowRead;
            }
        }
        // #endregion
        return tInstance;
    }
    // #endregion
    // #region Sync
    getItem(permission, option, right, args = null) {
        // #region Check
        if (!permission) {
            return reactive({
                key: "$",
                isAllowed: true
            });
        }
        // #endregion
        // #region Optimize
        let tPermission = undefined;
        {
            if (utils.isString(permission)) {
                tPermission = {
                    permission: permission.toString(),
                    option: option ?? "Form",
                    params: []
                };
            }
            else {
                tPermission = permission;
            }
        }
        // #endregion
        // #region Instance
        let tInstance = {
            securityKey: "",
            permission: tPermission.permission,
            option: tPermission.option,
            AllowRead: false,
            AllowCreate: false,
            AllowDelete: false,
            AllowEdit: false,
            params: {}
        };
        if (tPermission.params && tPermission.params.length > 0) {
            // #region Build
            tPermission.params.forEach(f => {
                let tValue = undefined;
                {
                    if (f.member) {
                        tValue = utils.getObjectValue(args, f.member);
                    }
                    else if (f.expression) {
                        tValue = utils.evalParams(f.expression, { self: args });
                    }
                    else if (f.value) {
                        tValue = f.value;
                    }
                }
                tInstance.params[f.name] = tValue;
            });
            // #endregion
        }
        var tUrlParams = new URLSearchParams();
        {
            if (tInstance.params) {
                Object.keys(tInstance.params).forEach(k => {
                    tUrlParams.set(k, tInstance.params[k]);
                });
            }
        }
        //Set
        {
            tInstance.securityKey = (tInstance.permission + "__" + tInstance.option + (tUrlParams.size > 0 ? "__" + tUrlParams.toString() : '')).toLowerCase();
        }
        // #endregion
        // #region Queue
        let t1 = reactive({
            key: tInstance.securityKey,
            right: right,
            isAllowed: false
        });
        //Append
        {
            this._queue.value.push(t1);
        }
        // #endregion
        // #region Push
        let tCachedItem = this._cache.get(tInstance.securityKey);
        {
            if (!tCachedItem) {
                this._cache.set(tInstance.securityKey, tInstance);
            }
        }
        //Run
        {
            this.evaluate(t1.key);
        }
        // #endregion
        return t1;
    }
}
